import { useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import COLOR from 'constants/color';
import { Add } from 'assets/icons/revamp';
import YouTube from 'assets/icons/YouTube';
import Vimeo from 'assets/icons/Vimeo';
import { t } from 'services/i18n.service';
import { updateMoreOptionURLs } from 'store/reducer/modal/slice';
import { KEY_BOARD, MODAL_TYPES } from 'constants/general';
import { isYoutubeURL, isVimeoURL } from 'utils/validators';
import { fetchYoutubeURLMetadata } from 'store/reducer/cache/extra';
import { updateIsYouTubeURLMetadataUpdating } from 'store/reducer/cache/slice';
import { useAppSelectorShallow } from 'store/hooks';
import { moreOptionModalVideoURLsSelector } from 'store/reducer/modal/more-options.modal.selectors';
import BaseButton from 'components/ui/BaseButton';
import { toast } from 'react-toastify';

const initialVideoUrlState = {
  url: '',
  isValid: false
};

const AddVideo: React.FC = () => {
  const dispatch = useAppDispatch();
  const { video_urls } = useAppSelectorShallow(
    moreOptionModalVideoURLsSelector
  );
  const [videoUrlState, setVideoUrlState] = useState(initialVideoUrlState);

  const handleAddVideo = () => {
    const trimmedUrl = videoUrlState.url.trim();
    if (!trimmedUrl) return;

    if (video_urls.includes(trimmedUrl)) {
      toast.error(t('tabs_revamp.videos_tab.video_url_already_added'));
      return;
    }

    const newUrls = [...video_urls, trimmedUrl];
    dispatch(
      updateMoreOptionURLs({
        modal_type: MODAL_TYPES.VIDEOS_URLS,
        urls: newUrls
      })
    );

    if (isYoutubeURL(trimmedUrl)) {
      dispatch(updateIsYouTubeURLMetadataUpdating(true));
      dispatch(fetchYoutubeURLMetadata(newUrls));
    }

    setVideoUrlState(initialVideoUrlState);
  };

  return (
    <div className='flex flex-col ~gap-3/4'>
      <div className='flex flex-col gap-0.5'>
        <div className='flex items-center ~gap-2/2.5'>
          <div className='flex items-center ~gap-1/1.5 flex-shrink-0'>
            <YouTube className='~w-2/6 ~h-2/6' />
            <Vimeo className='~w-2/4 ~h-2/4' />
          </div>
          <h3 className='~text-xs/base font-semibold flex flex-col'>
            {t('tabs_revamp.videos_tab.add_videos')}
          </h3>
        </div>
        <p className='~text-[0.625rem]/xs text-gray-600 dark:text-gray-400'>
          {t('tabs_revamp.videos_tab.add_videos_description')}
        </p>
      </div>
      <div className='flex items-center ~gap-2/3'>
        <div className='flex-1'>
          <FloatingLabelInput
            type='text'
            placeholder={t('tabs_revamp.videos_tab.video_url')}
            value={videoUrlState.url}
            onChange={(e) =>
              setVideoUrlState({
                url: e.target.value,
                isValid:
                  isYoutubeURL(e.target.value) || isVimeoURL(e.target.value)
              })
            }
            onKeyDown={(e) => {
              if (e.key === KEY_BOARD.ENTER.KEY) {
                handleAddVideo();
              }
            }}
            size='small'
            variant='outlined'
            showLabel={true}
            label=''
            inputClassName='!py-1.5'
          />
        </div>
        <BaseButton
          title={t('tabs_revamp.videos_tab.add_video')}
          onClick={handleAddVideo}
          size='sm'
          variant='secondary'
          icon={<Add styles='~w-3/4 ~h-3/4' fill={COLOR.PRIMARY.LIGHT_900} />}
          disabled={!videoUrlState.isValid}
        />
      </div>
    </div>
  );
};

export default AddVideo;
